import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { APP_VERSION } from "@/lib/version";
import { ArrowClockwise, Sparkle, X } from "@phosphor-icons/react";

/**
 * App-wide "new version available" banner.
 *
 * Polls `/api/version` every 10 s and compares the backend's reported
 * (version, build_id) with what this JS bundle knows about. If they
 * diverge, a sticky yellow banner appears at the top of every page.
 *
 * v1.28.59 hardening (see BUG report):
 *   - Backend `build_id` is now derived from server.py's mtime, so all
 *     Kubernetes replicas of a deploy return the same value (no more
 *     false-positive banner every poll).
 *   - Dismiss persists in `localStorage` keyed on (version + build_id).
 *     The banner reappears ONLY when a genuinely newer (version|build_id)
 *     is served AND the user hasn't already dismissed that exact pair.
 *   - Countdown restored to 60 s (was 10 s).
 */

const LS_KEY = "micm.dismissedUpdate";      // JSON: {version, buildId}
const AUTO_RELOAD_SECS = 60;

function readDismissed() {
    try {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        const j = JSON.parse(raw);
        if (j && typeof j === "object") return { version: j.version || "", buildId: j.buildId || "" };
    } catch { /* ignore */ }
    return null;
}

function writeDismissed(version, buildId) {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify({ version: version || "", buildId: buildId || "" }));
    } catch { /* quota / private mode */ }
}

export default function UpdateBanner() {
    const [backendVersion, setBackendVersion] = useState(null);
    const [backendBuildId, setBackendBuildId] = useState(null);
    // First build_id this tab saw — used to detect a redeploy even when
    // APP_VERSION wasn't bumped in version.js.
    const initialBuildIdRef = useRef(null);
    // Local (in-memory) dismissal — starts from localStorage so a page
    // reload does not un-dismiss.
    const [dismissedPair, setDismissedPair] = useState(() => readDismissed());
    const [reloading, setReloading] = useState(false);

    const check = useCallback(async () => {
        try {
            const { data } = await api.get("/version");
            if (data?.version) setBackendVersion(data.version);
            if (data?.build_id) {
                setBackendBuildId(data.build_id);
                if (initialBuildIdRef.current === null) initialBuildIdRef.current = data.build_id;
            }
        } catch { /* silent — server may be offline */ }
    }, []);

    useEffect(() => {
        check();
        const id = setInterval(check, 10 * 1000);
        const onFocus = () => check();
        const onVis = () => { if (document.visibilityState === "visible") check(); };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [check]);

    // Service-worker / autoUpdate.js signals (still honoured as a fast path).
    const [swUpdateReady, setSwUpdateReady] = useState(false);
    useEffect(() => {
        if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
        const onMsg = (ev) => { if (ev?.data?.type === "SW_UPDATED") setSwUpdateReady(true); };
        const onCtrl = () => setSwUpdateReady(true);
        const onEvent = () => setSwUpdateReady(true);
        navigator.serviceWorker.addEventListener("message", onMsg);
        navigator.serviceWorker.addEventListener("controllerchange", onCtrl);
        window.addEventListener("micm:update-detected", onEvent);
        return () => {
            navigator.serviceWorker.removeEventListener("message", onMsg);
            navigator.serviceWorker.removeEventListener("controllerchange", onCtrl);
            window.removeEventListener("micm:update-detected", onEvent);
        };
    }, []);

    // ----- Compute "should show banner?" -----
    const versionMismatch = !!(backendVersion && backendVersion !== APP_VERSION);
    const buildIdMismatch = !!(backendBuildId && initialBuildIdRef.current && backendBuildId !== initialBuildIdRef.current);
    const rawShouldShow = versionMismatch || swUpdateReady || buildIdMismatch;

    // Suppress if the user already dismissed this exact (version, build_id)
    // pair. The banner will reappear as soon as either field advances.
    const dismissedMatch = !!(dismissedPair
        && dismissedPair.version === (backendVersion || "")
        && dismissedPair.buildId === (backendBuildId || ""));
    const shouldShow = rawShouldShow && !dismissedMatch;

    // ----- Arm the countdown exactly once per fresh "shouldShow" transition
    const [secsLeft, setSecsLeft] = useState(AUTO_RELOAD_SECS);
    const armedRef = useRef(false);
    useEffect(() => {
        if (!shouldShow) {
            // Banner hidden → make sure timer can be re-armed next time
            // (e.g. after another deploy after a Dismiss).
            armedRef.current = false;
            setSecsLeft(AUTO_RELOAD_SECS);
            return;
        }
        if (armedRef.current) return;
        armedRef.current = true;
        setSecsLeft(AUTO_RELOAD_SECS);
        const tick = setInterval(() => {
            setSecsLeft((n) => {
                if (n <= 1) {
                    clearInterval(tick);
                    forceRefresh();
                    return 0;
                }
                return n - 1;
            });
        }, 1000);
        return () => clearInterval(tick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    const forceRefresh = async () => {
        setReloading(true);
        try {
            if ("serviceWorker" in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
            }
            if (window.caches) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
            }
        } catch { /* non-fatal */ }
        window.location.replace(window.location.pathname + "?_v=" + Date.now());
    };

    const dismiss = () => {
        // Persist the CURRENT deployed pair so we ignore it until a truly
        // newer one arrives. Cancels the countdown.
        writeDismissed(backendVersion || "", backendBuildId || "");
        setDismissedPair({ version: backendVersion || "", buildId: backendBuildId || "" });
        armedRef.current = false;
        setSecsLeft(AUTO_RELOAD_SECS);
    };

    if (!shouldShow) return null;

    return (
        <div
            className="update-banner sticky top-0 z-50 flex flex-wrap items-center justify-center gap-2 border-b border-amber-300 bg-gradient-to-r from-amber-300 via-amber-200 to-amber-300 px-3 py-2 text-center text-sm font-medium text-amber-950 shadow-sm"
            data-testid="update-banner"
            role="status"
        >
            <Sparkle size={16} weight="fill" className="shrink-0 text-amber-800" />
            <button
                type="button"
                onClick={forceRefresh}
                disabled={reloading}
                data-testid="update-banner-text"
                className="cursor-pointer bg-transparent border-0 p-0 text-left text-sm font-medium text-amber-950 hover:underline disabled:opacity-70"
            >
                A new update is available. {reloading ? "Refreshing…" : `Refreshing in ${secsLeft} second${secsLeft === 1 ? "" : "s"}…`}
            </button>
            <button
                type="button"
                onClick={forceRefresh}
                disabled={reloading}
                data-testid="update-banner-refresh"
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-800 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-sm hover:bg-amber-900 disabled:opacity-50"
            >
                <ArrowClockwise size={12} weight="bold" className={reloading ? "animate-spin" : ""} />
                {reloading ? "Refreshing…" : "Refresh Now"}
            </button>
            <button
                type="button"
                onClick={dismiss}
                data-testid="update-banner-dismiss"
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-800 bg-transparent px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-900 hover:bg-amber-300/60"
                title="Dismiss (banner will reappear when another new version is released)"
            >
                <X size={12} weight="bold" />
                Dismiss
            </button>
        </div>
    );
}
